import LoadingButton from '@/common/components/base/button/loadingButton';
import CheckBox from '@/common/components/base/checkBox/CheckBox';
import RadioGroup from '@/common/components/base/input/radioGroup/RadioGroup';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import Typography from '@/common/components/base/typography/Typography';
import MainLayout from '@/common/components/layouts/MainLayout';
import { GetVisitFormDataQueryResponseType } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVisitFormData/getVisitFormDataQuery.type';
import { getVisitFormDataQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVisitFormData/getVisitFormDataQueryOptions';
import { usePatchVisitFormQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchVisitForm/patchVisitFormQueryOptions';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';

export default ({ isEditable = true }: { isEditable: boolean }) => {
  return (
    <MainLayout title="بیمه گذار / کارشناسی" header="titleHeader">
      <VisitForm isEditable={isEditable} />
    </MainLayout>
  );
};

const VisitForm = ({ isEditable = true }: { isEditable: boolean }) => {
  const { id } = useParams();
  const { data } = useQuery(getVisitFormDataQueryOptions({ id: id! }));
  const { mutate: patchVisitForm, isPending } = usePatchVisitFormQueryOptions();
  const methods = useForm();
  const { setValue } = methods;
  const navigate = useNavigate();

  useEffect(() => {
    if (data) {
      data.forEach((item: any) => {
        item.radio_groups?.forEach((groupArr: any[], idx: number) => {
          const selected = Array.isArray(groupArr)
            ? groupArr.find((r: any) => r.status)?.name
            : undefined;
          if (selected) {
            setValue(`radio_groups.${item.name}.${idx}`, selected);
          }
        });
      });
    }
  }, [data, setValue]);

  const onSubmit = (formData: any) => {
    const result = data?.map((item: any) => {
      const radio_groups = item.radio_groups.map(
        (groupArr: any[], idx: number) => {
          const selected = formData.radio_groups?.[item.name]?.[idx];
          return groupArr.map((radio: any) => ({
            ...radio,
            status: radio.name === selected,
          }));
        },
      );

      // checkboxes
      const checkboxes = item.checkboxes.map((checkbox: any) => ({
        ...checkbox,
        status: !!formData.checkboxes?.[item.name]?.[checkbox.name],
      }));

      return { ...item, radio_groups, checkboxes };
    });

    patchVisitForm(
      { id: id!, data: result ?? [] },
      {
        onSuccess: () => {
          navigate(-1);
        },
      },
    );
  };

  return (
    <Box width="100%" pt={'24px'} position="relative">
      <Box pb="4px">
        <TitleDescription>فرم بازدید</TitleDescription>
      </Box>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          {data?.map((item: GetVisitFormDataQueryResponseType) => (
            <VisitFormCardComponent key={item?.name} title={item.fa_name || ''}>
              {item.radio_groups?.map((radioGroupArr, idx) => (
                <Box key={idx}>
                  <RenderRadioGroup
                    radioGroupArr={
                      Array.isArray(radioGroupArr)
                        ? radioGroupArr
                        : [radioGroupArr]
                    }
                    disabled={isEditable ? false : true}
                    groupName={`radio_groups.${item.name}.${idx}`}
                    defaultValue={
                      Array.isArray(radioGroupArr)
                        ? radioGroupArr.find((r) => r.status)?.name
                        : undefined
                    }
                  />
                </Box>
              ))}
              <Flex justifyContent="space-between">
                {item.checkboxes?.map((checkboxGroup) => (
                  <RenderCheckboxGroup
                    key={checkboxGroup.id}
                    checkboxGroup={checkboxGroup}
                    name={`checkboxes.${item.name}.${checkboxGroup.name}`}
                    defaultChecked={checkboxGroup.status}
                    disabled={isEditable ? false : true}
                  />
                ))}
              </Flex>
            </VisitFormCardComponent>
          ))}
          {isEditable && (
            <LoadingButton
              loading={isPending}
              fullWidth
              type="submit"
              mt="16px"
            >
              تایید و ادامه
            </LoadingButton>
          )}
        </form>
      </FormProvider>
    </Box>
  );
};

const VisitFormCardComponent = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <Box borderRadius="8px" bgColor="white" boxShadow="cardShadow" mt="16px">
      <Box py={9} px={16}>
        <Box bgColor="purple-6" borderRadius="4px" px={8} width="fit-content">
          <Typography textStyle="b4">{title}</Typography>
        </Box>
      </Box>
      <Box borderBottom="1px solid" borderColor="gray-2"></Box>
      {children}
    </Box>
  );
};

const RenderRadioGroup = ({
  radioGroupArr,
  groupName,
  disabled = false,
}: {
  radioGroupArr: {
    id: number;
    name: string;
    fa_name: string;
    status: boolean;
  }[];
  groupName: string;
  defaultValue?: string;
  disabled?: boolean;
}) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      p={16}
      flexDirection="row"
      borderBottom="1px solid"
      borderColor="gray-2"
    >
      <RadioGroup
        options={radioGroupArr.map((item) => ({
          value: item.name,
          label: item.fa_name,
        }))}
        name={groupName}
        renderRadioButton={(Component) => (
          <Box className={css({ w: '100%' })}>{Component}</Box>
        )}
        disabled={disabled}
        wrapperClassName={css({
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
        })}
      />
    </Box>
  );
};

const RenderCheckboxGroup = ({
  checkboxGroup,
  name,
  defaultChecked,
  disabled = false,
}: {
  checkboxGroup: { id: number; name: string; fa_name: string; status: boolean };
  name: string;
  defaultChecked?: boolean;
  disabled?: boolean;
}) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      p={16}
      flexDirection="row"
      width="50%"
    >
      <CheckBox disabled={disabled} name={name} defaultValue={defaultChecked} />
      <Typography textStyle="b1" mr={8}>
        {checkboxGroup?.fa_name}
      </Typography>
    </Box>
  );
};
